import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { combineLatest, forkJoin } from 'rxjs';
import { first, take, timeout } from 'rxjs/operators';
import { NotificationLink, NotificationScopeType } from '../models/notification';
import { AGRI_UNASSIGNED_NAVIGATION_TYPE, INTRUSION_EVENT_NAVIGATION_TYPE, NavigationInfo } from '../models/navigationExtra';
import { Domain } from '../models/domain';
import { ConfirmationDialogData } from '../models/confirmationDialogData.model';
import { ConfirmationDialogComponent } from '../dialogs/confirmation-dialog/confirmation-dialog.component';
import { MainSubscriptionsService } from './main-subscriptions/main-subscriptions.service';
import { PassDataService } from './pass-data/pass-data.service';
import { ResourceAccessService } from './resource-access.service';
import { ServiceEntitlementService } from './service-entitlement.service';
import { AuthState } from '../../store/auth/auth.reducer';
import * as AuthSelectors from '../../store/auth/auth.selectors';
import * as AuthActions from '../../store/auth/auth.actions';
import * as MainActions from '../../store/main/main.actions';

interface LinkTarget {
  route: string;
  navigation: NavigationInfo;
  /** Where "back" leads when the page is opened in a freshly switched domain. */
  serviceRoute: string;
}

const SCOPE_TYPES: readonly NotificationScopeType[] = ['group', 'device', 'domain', 'event'];

// What the device page loads first: without it the page cannot open.
const DEVICE_DETAIL_API = 'uv.device.detail.api';

// Domain switch is a chain of four calls; past this the page is not opened.
const DOMAIN_SWITCH_TIMEOUT_MS = 20000;

/**
 * Opens the page an alert came from. Shared by the bell, the alert popup and
 * the push tap, so all three land on the same page.
 */
@Injectable({ providedIn: 'root' })
export class NotificationLinkService {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store<AuthState>);
  private readonly actions$ = inject(Actions);
  private readonly mainService = inject(MainSubscriptionsService);
  private readonly passDataService = inject(PassDataService);
  private readonly entitlement = inject(ServiceEntitlementService);
  private readonly resourceAccess = inject(ResourceAccessService);

  public fromStorage(stored: string | null | undefined): NotificationLink | null {
    const parts: string[] = (stored ?? '').split(':');
    return parts.length === 4 ? create(parts[0], parts[1], parts[2], parts[3]) : null;
  }

  /** From the push data payload, which carries the same four fields by name. */
  public fromData(data: Record<string, unknown> | null | undefined): NotificationLink | null {
    return data ? create(data['category'], data['scopeType'], data['scopeId'], data['domainId']) : null;
  }

  /** Whether a stored link and a link point at the same page. */
  public matches(stored: string | null | undefined, link: NotificationLink): boolean {
    const other: NotificationLink | null = this.fromStorage(stored);
    return !!other
      && other.category === link.category
      && other.scopeType === link.scopeType
      && sameId(other.scopeId, link.scopeId)
      && sameId(other.domainId, link.domainId);
  }

  public canOpen(link: NotificationLink | null): boolean {
    return !!link && !!this.resolve(link);
  }

  /** The page behind a "go to page" action; null without one, or when the app already shows it. */
  public pageToOpen(stored: string | null | undefined): NotificationLink | null {
    const link: NotificationLink | null = this.fromStorage(stored);
    return link && this.canOpen(link) && !this.isCurrentPage(link) ? link : null;
  }

  /** The phrase of the button that opens the link: it says where it leads. */
  public actionLabel(link: NotificationLink | null): string {
    const target: LinkTarget | null = link ? this.resolve(link) : null;

    switch (target?.route) {
      case 'intrusion-site':
        return target.navigation.Type === INTRUSION_EVENT_NAVIGATION_TYPE ? 'NOTIFICATION.OPEN_ALARM' : 'NOTIFICATION.OPEN_SITE';
      case 'device':
        return 'NOTIFICATION.OPEN_DEVICE';
      case 'smart-parking-detail':
        return 'NOTIFICATION.OPEN_PARKING';
      case 'agrivoltaic-detail':
        return target.navigation.Type === AGRI_UNASSIGNED_NAVIGATION_TYPE ? 'NOTIFICATION.OPEN_UNASSIGNED' : 'NOTIFICATION.OPEN_GROUP';
      default:
        return 'GENERAL.GO_TO_PAGE';
    }
  }

  /** The link's domain when it is not the current one and the user can open it; null otherwise. */
  public otherDomain(link: NotificationLink | null): Domain | null {
    if (!link) {
      return null;
    }

    let other: Domain | null = null;
    combineLatest([
      this.store.select(AuthSelectors.getDomain),
      this.store.select(AuthSelectors.getDomains),
    ]).pipe(take(1)).subscribe(([current, domains]) => {
      if (!sameId(current?.Id, link.domainId)) {
        other = (domains ?? []).find(d => sameId(d.Id, link.domainId)) ?? null;
      }
    });
    return other;
  }

  /** `confirmSwitch` false when the caller's own button already names the domain. */
  public open(link: NotificationLink, confirmSwitch: boolean = true): void {
    const target: LinkTarget | null = this.resolve(link);
    if (!target) {
      return;
    }

    combineLatest([
      this.store.select(AuthSelectors.getDomain),
      this.store.select(AuthSelectors.getDomains),
    ]).pipe(take(1)).subscribe(([current, domains]) => {
      if (sameId(current?.Id, link.domainId)) {
        this.navigate(target, false);
        return;
      }

      const domain: Domain | undefined = (domains ?? []).find(d => sameId(d.Id, link.domainId));
      if (!domain) {
        this.mainService.setCustomErrorComand('SNACK_BAR.NOT_ALLOWED_TO_SEE_PAGE');
        return;
      }

      if (confirmSwitch) {
        this.confirmDomainSwitch(domain, target);
      } else {
        this.switchDomainAndNavigate(domain, target);
      }
    });
  }

  private resolve(link: NotificationLink): LinkTarget | null {
    switch (`${link.category}/${link.scopeType}`) {
      case 'agrivoltaic/group':
        return {
          route: 'agrivoltaic-detail',
          navigation: { Id: link.scopeId },
          serviceRoute: 'agrivoltaic-dashboard',
        };
      // The rule engine links the device only when it belongs to no group.
      case 'agrivoltaic/device':
        return {
          route: 'agrivoltaic-detail',
          navigation: { Type: AGRI_UNASSIGNED_NAVIGATION_TYPE },
          serviceRoute: 'agrivoltaic-dashboard',
        };
      case 'parking/device':
        return {
          route: 'smart-parking-detail',
          navigation: { Id: link.scopeId },
          serviceRoute: 'smart-parking',
        };
      case 'intrusion/event':
        return {
          route: 'intrusion-site',
          navigation: { Id: link.scopeId, Type: INTRUSION_EVENT_NAVIGATION_TYPE },
          serviceRoute: 'intrusion-dashboard',
        };
      // Burst summary: the scope is the site (gateway).
      case 'intrusion/device':
        return {
          route: 'intrusion-site',
          navigation: { Id: link.scopeId },
          serviceRoute: 'intrusion-dashboard',
        };
      // Gateway or camera status: the site where intrusion is active (judged on
      // the current domain), else the device page for who may open it, else no link.
      case 'edge/device':
        if (this.entitlement.isServiceTypeActive('intrusion')) {
          return { route: 'intrusion-site', navigation: { Id: link.scopeId }, serviceRoute: 'intrusion-dashboard' };
        }
        return this.resourceAccess.canAccess(DEVICE_DETAIL_API)
          ? { route: 'device', navigation: { Id: link.scopeId }, serviceRoute: 'dashboard' }
          : null;
      default:
        return null;
    }
  }

  // Same route, entity and domain. Both sources replay synchronously.
  private isCurrentPage(link: NotificationLink): boolean {
    const target: LinkTarget | null = this.resolve(link);
    if (!target || this.currentRoute() !== target.route) {
      return false;
    }

    let shown: boolean = false;
    combineLatest([
      this.store.select(AuthSelectors.getDomain),
      this.passDataService.navigationInfo$,
    ]).pipe(take(1)).subscribe(([domain, navigation]) => {
      shown = sameId(domain?.Id, link.domainId)
        && (!target.navigation.Id || sameId(navigation?.Id, target.navigation.Id))
        && (!target.navigation.Type || navigation?.Type === target.navigation.Type);
    });
    return shown;
  }

  private confirmDomainSwitch(domain: Domain, target: LinkTarget): void {
    const data: ConfirmationDialogData = {
      title: 'NOTIFICATION.SWITCH_DOMAIN_TITLE',
      description: 'NOTIFICATION.SWITCH_DOMAIN_DESCRIPTION',
      descriptionParams: { domain: domain.Name },
      confirmBtnLabel: 'NOTIFICATION.SWITCH_DOMAIN_CONFIRM',
      cancelBtnLabel: 'GENERAL.CANCEL',
    };

    this.dialog.open(ConfirmationDialogComponent, { data, autoFocus: false })
      .afterClosed()
      .pipe(first())
      .subscribe((confirmed: boolean | undefined) => {
        if (confirmed === true) {
          this.switchDomainAndNavigate(domain, target);
        }
      });
  }

  private switchDomainAndNavigate(domain: Domain, target: LinkTarget): void {
    // setUserInfo closes the switch chain, so the route guards judge the new
    // domain; the new roles also restart the main data chain, whose goToMain
    // returns to the dashboard. The page opens after both. A failing chain shows
    // its own error; setError is no stop signal, reloading pages raise it too.
    forkJoin([
      this.actions$.pipe(ofType(AuthActions.setUserInfo), take(1)),
      this.actions$.pipe(ofType(MainActions.goToMain), take(1)),
    ]).pipe(
      timeout(DOMAIN_SWITCH_TIMEOUT_MS),
    ).subscribe({
      next: () => this.navigate(target, true),
      error: () => undefined,
    });

    this.mainService.setCurrentDomainPropertyComand(domain);
  }

  private navigate(target: LinkTarget, domainSwitched: boolean): void {
    if (domainSwitched) {
      // The back stack points at pages of the previous domain.
      this.mainService.setNavigationInfoComand();
    }

    const backRoute: string = domainSwitched ? target.serviceRoute : this.currentRoute();
    this.mainService.setNavigationInfoComand({ ...target.navigation, BackRoute: backRoute });

    // Through invisible-child so the detail page is rebuilt even when it is
    // already open on another entity.
    this.router.navigate(['main/invisible-child'], { skipLocationChange: true })
      .then(() => this.router.navigate([`main/${target.route}`]));
  }

  private currentRoute(): string {
    const path: string = this.router.url.split(/[?#;]/)[0];
    return path.startsWith('/main/') ? path.substring('/main/'.length) : 'dashboard';
  }
}

function create(category: unknown, scopeType: unknown, scopeId: unknown, domainId: unknown): NotificationLink | null {
  const parts: unknown[] = [category, scopeType, scopeId, domainId];
  if (parts.some(p => typeof p !== 'string' || !p)) {
    return null;
  }
  if (!SCOPE_TYPES.includes(scopeType as NotificationScopeType)) {
    return null;
  }

  return {
    category: category as string,
    scopeType: scopeType as NotificationScopeType,
    scopeId: scopeId as string,
    domainId: domainId as string,
  };
}

function sameId(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && a.toLowerCase() === b.toLowerCase();
}
